import { formatDate } from '@angular/common';
import {
    Component,
    effect,
    EventEmitter,
    inject,
    InjectionToken,
    Injector,
    input,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Type,
} from '@angular/core';
import { Router } from '@angular/router';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { combineLatest, concat, of, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AanAfwezigheid } from 'src/app/afas-classes/aanafwezigheid.class';
import { Hourtype } from 'src/app/afas-classes/hourtype';
import { FgWeekDay } from 'src/app/classes/fg.weekday.class';
import { RealisationHourtype } from 'src/app/classes/realisation';
import { Settlement, SettlementHourtype } from 'src/app/classes/settlement.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { PeriodType, PLATFORM } from 'src/app/environment';
import { AfasService } from 'src/app/services/afas.service';
import { ApiResponse } from 'src/app/services/api/api.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { Address, LocationService } from 'src/app/services/location.service';
import { PeriodService } from 'src/app/services/period.service';
import { RealisationService } from 'src/app/services/realisation.service';
import {
    NON_TIME_HOURTYPES,
    SettlementsService,
    SUPPLEMENT_CAO_HOURS_TEXT,
} from 'src/app/services/settlements.service';
import { Utils } from 'src/app/utils.class';
import { environment } from '../../../environments/environment';
import { Week } from '../../classes/week';
import { WeekDay } from '../../classes/weekday';
import { DvdsSettlementService } from './dvds-settlement.service';
import { Settleable } from './settleable.interface';
import { TracklineSettlementService } from './trackline-settlement.service';

const PlatformSettlementWeekService = new InjectionToken<Settleable>('PlatformSettlementWeekService');

const serviceMapping: Record<PLATFORM, Type<Settleable>> = {
    trackline: TracklineSettlementService,
    dvds: DvdsSettlementService,
    buitengewoonbv: DvdsSettlementService,
};

/**
 * Weken die verloond mogen worden. Alle zichtbare weken komen in aanmerking, ook de eerste en
 * laatste week die buiten de kalendermaand doorlopen; `sendToAfas` is leidend, want een week
 * die al naar AFAS is verzonden mag niet opnieuw verloond worden.
 */
export function weeksToSettle(weeks: Week[]) {
    return weeks.filter(week => !week.sendToAfas);
}

@Component({
    selector: 'app-period-settlement',
    templateUrl: './period-settlement.component.html',
    styleUrls: ['./period-settlement.component.scss'],
    standalone: false,
    providers: [
        TracklineSettlementService,
        DvdsSettlementService,
        {
            provide: PlatformSettlementWeekService,
            useFactory: (injector: Injector) => injector.get<Settleable>(serviceMapping[environment.platform]),
            deps: [Injector],
        },
    ],
})
export class PeriodSettlementComponent extends CodaltComponent implements OnInit, OnChanges, OnDestroy {
    @Input() print = false;
    @Input() beginDate: Date;
    @Input() endDate: Date;
    user = input<User>();
    @Input() daytypeCounts: { type: string; count: number; minutes: number }[];
    @Output() settlingChange = new EventEmitter<boolean>();

    hourtypes: Hourtype[];
    dayDetail: WeekDay | null = null;
    hoverWeekButton?: string;
    weeks: Week[];
    settlements: Settlement[];
    aanAfwezigheid: AanAfwezigheid[];
    fcNamePeriodTotalMap: Map<string, number>;

    userSubscriptions = new Subscription();
    secondLineHourTypeCodes = [
        SettlementHourtype.sleep,
        SettlementHourtype.sleepWeekend,
        SettlementHourtype.paid_leave,
        SettlementHourtype.bank_holiday,
        SettlementHourtype.short_leave,
        SettlementHourtype.special_leave,
    ];
    secondLineRealisationHourTypes = [
        RealisationHourtype.sleep,
        RealisationHourtype.paid_leave,
        RealisationHourtype.bank_holiday,
        RealisationHourtype.short_absence,
        RealisationHourtype.special_leave,
    ];
    caoBouwInfra = false;
    settling = false;
    resettleRequired = false;
    cancelSettling = false;
    invalidTimezone = false;
    showTravelTo = false;
    PeriodType = PeriodType;
    dateFormatEquals = Utils.dateFormatEquals;

    /**
     * Wat er op dit moment opgehaald wordt. De verlofdata komt uit Afas en is soms traag, terwijl
     * we die wel nodig hebben voordat er verloond kan worden. Daarom per bron bijhouden wat er
     * loopt, zodat de gebruiker ziet waarop gewacht wordt.
     */
    loading: { label: string; source: string }[] = [];

    /** Periode waarover de data geladen wordt, voor de melding tijdens het laden. */
    loadingRange?: { beginDate: Date; endDate: Date };

    employeeService = inject(EmployeeService);
    periodService = inject(PeriodService);
    settlementWeekService = inject<Settleable>(PlatformSettlementWeekService);

    constructor(
        private realisationService: RealisationService,
        private settlementService: SettlementsService,
        private confirmDialogService: ConfirmDialogService,
        private settlementsService: SettlementsService,
        private locationService: LocationService,
        private afasService: AfasService,
        private router: Router
    ) {
        super();

        effect(() => {
            this.settlementWeekService.employee = this.user();

            this.caoBouwInfra = this.employeeService.hasCaoBouwInfra(this.user());
        });

        this.settlementWeekService.updateForm = this.settlementsToForm.bind(this);

        if (new Date(2024, 0, 1).getTimezoneOffset() !== -60) {
            this.invalidTimezone = true;
        }
    }

    ngOnChanges() {
        this.getData();
    }

    ngOnInit() {
        // Readonly as print
        if (!AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)) {
            this.print = true;
        }
    }

    editHours(date: Date, userId: number) {
        this.router.navigate([
            `/hours/${formatDate(date, 'yyyy-MM-dd', 'nl')}/${userId}`,
            { backTo: window.location.pathname },
        ]);
    }

    private getData() {
        this.userSubscriptions?.unsubscribe();

        this.userSubscriptions = new Subscription();

        this.dayDetail = null;

        this.showTravelTo = false;

        this.loading = [];

        this.loadingRange = undefined;

        const getData = () => {
            // Hele weken ophalen: de laatste week van de periode loopt door in de volgende maand
            // en moet volledig verloond worden om de GU/MU-verdeling over de week te laten kloppen.
            const { beginDate, endDate } = this.periodService.getFullWeeksPeriodDates({
                beginDate: this.beginDate,
                endDate: this.endDate,
            });

            const leaves$ = AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)
                ? this.afasService
                      .getAanAfwezigheid(beginDate, endDate, this.user().afas_employee_id)
                      .pipe(finalize(() => this.finishLoading('leaves')))
                : of<ApiResponse<AanAfwezigheid[]> | null>(null);

            const userId = this.user().id;

            this.loadingRange = { beginDate, endDate };

            this.loading = [
                { source: 'realisations', label: 'Uren' },
                { source: 'settlements', label: 'Verrekende regels' },
            ];

            if (AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)) {
                this.loading.push({ source: 'leaves', label: 'Verlof uit Afas' });
            }

            this.userSubscriptions.add(
                combineLatest([
                    this.realisationService
                        .getUserSchedule(beginDate, endDate, userId)
                        .pipe(finalize(() => this.finishLoading('realisations'))),
                    this.settlementService
                        .getUserSettlements(beginDate, endDate, userId)
                        .pipe(finalize(() => this.finishLoading('settlements'))),
                    leaves$,
                ]).subscribe(([realisations, settlements, aanafwezigheid]) => {
                    if (!Array.isArray(aanafwezigheid?.data)) {
                        this.confirmDialogService
                            .confirm({
                                title: 'Geen verlof data uit afas',
                                text: 'Er kon geen verlofdata uit afas opgehaald worden. De verloning is niet compleet',
                                confirm: 'Sluiten',
                            })
                            .then(
                                () => {},
                                () => {}
                            );
                    }
                    this.settlementWeekService.aanAfwezigheid = aanafwezigheid?.data;
                    this.settlementWeekService.realisations = realisations.data ?? [];

                    this.settlements = settlements.data;

                    const oldestSettlementDate = this.settlements?.length
                        ? this.settlements.reduce((min, s) => {
                              const d = s.updated_at ? new Date(s.updated_at) : new Date(0);

                              return d < min ? d : min;
                          }, new Date())
                        : null;

                    this.resettleRequired =
                        oldestSettlementDate !== null &&
                        this.settlementWeekService.realisations.some(r => {
                            const realisationDate = r.updated_at ? new Date(r.updated_at) : new Date(0);

                            return realisationDate > oldestSettlementDate && !r.paid;
                        });

                    this.parseData();
                })
            );
        };

        if (this.settlementWeekService.hourTypes) {
            getData();
        } else {
            this.subscriptions.add(
                this.settlementsService.getHourtypes().subscribe(hourtypes => {
                    this.settlementWeekService.hourTypes = hourtypes.data;

                    getData();
                })
            );
        }
    }

    /**
     * Markeert een bron als klaar. Via finalize, zodat de melding ook verdwijnt als de call een
     * fout geeft; anders zou de laadbalk blijven staan terwijl er niets meer gebeurt.
     */
    private finishLoading(source: string) {
        this.loading = this.loading.filter(item => item.source !== source);
    }

    showDayDetails(event: MouseEvent, day: WeekDay) {
        event.preventDefault();

        const target = event.target as HTMLElement | null;
        const targetClassName = target?.className ?? '';

        if (targetClassName.includes('mat-mdc-button-touch-target')) {
            return;
        }

        if (this.print) {
            return;
        }

        if (this.dayDetail === day) {
            this.dayDetail = null;
            return;
        }

        this.dayDetail = day;
    }

    async settlePeriodHours(week?: Week) {
        /**
         * Niet verlonen zolang de data nog binnenkomt: zonder de verlofdata uit Afas zou de
         * verrekening incompleet zijn. De knoppen zijn dan uitgeschakeld, dit is de vangnet.
         */
        if (this.loading.length) {
            return;
        }

        this.cancelSettling = false;
        this.settling = !week;

        this.settlingChange.emit(this.settling);

        let failed = false;

        const weeks = week ? [week] : weeksToSettle(this.weeks);

        try {
            for (const week of weeks) {
                if (this.cancelSettling) {
                    break;
                }

                try {
                    await this.settlementWeekService.settleWeekHours(week);
                } catch (error) {
                    failed = true;

                    console.error(`Week ${week.weeknumber} kon niet worden verloond`, error);

                    break;
                }
            }

            if (!failed && !this.cancelSettling) {
                this.resettleRequired = false;
            }
        } finally {
            this.settling = false;

            this.cancelSettling = false;

            this.settlingChange.emit(this.settling);
        }
    }

    cancelSettlePeriodHours() {
        this.cancelSettling = true;
    }

    removeWeek(week: Week) {
        if (!week.settling) {
            week.settling = true;
            this.confirmDialogService
                .confirm({
                    title: 'Wil je alle nacalculatieregels verwijderen?',
                    text: `We verwijderen alle nacalculatieregels voor ${this.user().name} in week ${week.weeknumber} uit Afas.<br>Doe dit alleen als deze periode nog niet verloond is.<br><b>Deze actie kan niet teruggedraaid worden.</b>`,
                    confirm: 'Regels verwijderen',
                    cancel: 'Annuleren',
                })
                .then(
                    () => {
                        const all$ = [];
                        let loadCount = 0;
                        week.weekDays[0].settling = true;
                        week.weekDays.forEach(weekDay => {
                            all$.push(this.settlementsService.deleteDay(weekDay.date, this.user().id));
                        });
                        concat(...all$).subscribe(() => {
                            week.weekDays[loadCount].settling = false;
                            loadCount++;
                            if (loadCount === week.weekDays.length) {
                                week.settling = false;
                                this.hoverWeekButton = null;
                                this.getData();
                            } else {
                                week.weekDays[loadCount].settling = true;
                            }
                        });
                    },
                    () => {
                        week.settling = false;
                    }
                );
        }
    }

    private settlementsToForm(settlements: Settlement[], day: WeekDay) {
        settlements.forEach(settlement => {
            settlement.hourtype = this.settlementWeekService.hourTypes.find(h => h.code === settlement.hourtype_code);

            const fcName = this.settlementWeekService.fcHourTypeMap.get(settlement.hourtype_code);

            let fg = day.fg;

            if (this.secondLineHourTypeCodes.includes(settlement.hourtype_code as SettlementHourtype)) {
                fg = day.fgSecond;
            }

            if (fcName) {
                const fc = fg.get(fcName) as FormControl<number>;

                let currentValue = fc.value ?? 0;

                currentValue += NON_TIME_HOURTYPES.includes(settlement.hourtype_code)
                    ? +settlement.amount
                    : settlement.minutes;

                fc.setValue(currentValue);
            }

            if (
                settlement.description === SUPPLEMENT_CAO_HOURS_TEXT &&
                ['GU', 'MU'].includes(settlement.hourtype_code)
            ) {
                const fc = fg.controls.supplemented;

                let currentValue = fc.value ?? 0;

                currentValue += NON_TIME_HOURTYPES.includes(settlement.hourtype_code)
                    ? +settlement.amount
                    : settlement.minutes;
                fc.setValue(currentValue);
            }

            if (
                settlement.hourtype_code === SettlementHourtype.travelHoursActual ||
                settlement.hourtype_code === SettlementHourtype.travelHours
            ) {
                let userAddress = settlement.description.replace(
                    this.locationService.formatAddress(this.user() as Address),
                    ''
                );

                userAddress = userAddress.replace(
                    this.locationService.formatAddress(this.user() as Address).replace(',', ''),
                    ''
                );

                day.travelTo = userAddress.replace('Reisuren van', '').replace(' naar ', '');

                this.showTravelTo = true;
            }
        });

        if (this.user().overtime) {
            if (day.date.getDay() > 0 && day.date.getDay() < 6) {
                day.fg.controls.normal.setValue(480 - day.fgSecond.value.normal);
                day.fg.controls.remaining.setValue(day.fg.value.hoursToReward);
            }
        } else {
            const remaining = day.fg.value.hoursToReward
                ? Math.max(day.fg.value.hoursToReward, day.fg.value.contractHours) - day.fg.value.contractHours
                : 0;

            if (day.date.getDay() > 0 && day.date.getDay() < 6) {
                const muMinutes = settlements
                    .filter(s => s.hourtype_code === 'MU' && s.description !== SUPPLEMENT_CAO_HOURS_TEXT)
                    .reduce((sum, current) => sum + current.minutes, 0);

                day.fg.controls.remaining.setValue(remaining - day.fg.value.notPaidHours + muMinutes);
            } else {
                day.fg.controls.remaining.setValue(remaining - day.fg.value.notPaidHours);
            }
        }
    }

    private parseData() {
        this.weeks = [];

        this.fcNamePeriodTotalMap = new Map<string, number>();

        this.daytypeCounts.forEach(dtc => {
            dtc.count = 0;
            dtc.minutes = 0;
        });

        const { beginDate, endDate } = this.periodService.getFullWeeksPeriodDates({
            beginDate: this.beginDate,
            endDate: this.endDate,
        });

        const bookdate = beginDate;
        const loopEndDate = new Date(endDate);

        /**
         * Grenzen van de periode zelf (de kalendermaand). De weken lopen daarbuiten door, maar
         * alleen dagen binnen deze grenzen tellen mee in de totalen onderaan.
         */
        const period = { beginDate: this.beginDate, endDate: this.endDate };

        function genFormGroup(
            dayType: string,
            beginDate: Date | string,
            endDate: Date | string,
            interruptions: number,
            totalMinutes: number,
            pause: number,
            hoursToReward: number,
            alreadyPaid: number
        ) {
            return new FormGroup<ControlsOf<ControlsOf<FgWeekDay>>>({
                dayType: new FormControl(dayType),
                begindate: new FormControl(beginDate ? formatDate(beginDate, 'H:mm', 'nl') : null),
                enddate: new FormControl(endDate ? formatDate(endDate, 'H:mm', 'nl') : null),
                interruptions: new FormControl(interruptions === 0 ? null : interruptions),
                hours: new FormControl(totalMinutes),
                pause: new FormControl(pause),
                hoursToReward: new FormControl(hoursToReward),
                notPaidHours: new FormControl(),
                contractHours: new FormControl(alreadyPaid),
                remaining: new FormControl(),
                supplemented: new FormControl(),

                traveltime: new FormControl(),
                travelcost: new FormControl(),
                travelrealisations: new FormControl(),

                normal: new FormControl(),
                bonus30: new FormControl(),
                bonus35: new FormControl(),
                bonus45: new FormControl(),
                bonus50: new FormControl(),
                bonus100: new FormControl(),

                overtime100: new FormControl(),
                overtime125: new FormControl(),
                overtime130: new FormControl(),
                overtime135: new FormControl(),
                overtime140: new FormControl(),
                overtime145: new FormControl(),
                overtime150s: new FormControl(),
                overtime150: new FormControl(),
                overtime200s: new FormControl(),
                overtime200: new FormControl(),
            });
        }

        let interval = 1;

        let week = new Week();

        while (bookdate.getTime() <= loopEndDate.getTime()) {
            if (interval === 4) {
                week.weeknumber = formatDate(bookdate, 'w', 'nl');
            }

            const weekDay = new WeekDay();

            const realisationsDay = this.settlementWeekService.realisations.filter(
                r => formatDate(r.bookdate, 'yyyy-MM-dd', 'nl') === formatDate(bookdate, 'yyyy-MM-dd', 'nl')
            );

            const settlementsDay = this.settlements.filter(
                r => formatDate(r.bookdate, 'yyyy-MM-dd', 'nl') === formatDate(bookdate, 'yyyy-MM-dd', 'nl')
            );

            const oldestSettlementDate = settlementsDay?.length
                ? settlementsDay.reduce((min, s) => {
                      const d = s.updated_at ? new Date(s.updated_at) : new Date(0);
                      return d < min ? d : min;
                  }, new Date())
                : null;

            weekDay.resettleRequired =
                oldestSettlementDate !== null &&
                realisationsDay?.some(r => {
                    const realisationDate = r.updated_at ? new Date(r.updated_at) : new Date(0);
                    return realisationDate > oldestSettlementDate && !r.paid;
                });

            const zzpLeave = realisationsDay.find(r => r.hourtype === RealisationHourtype.zzp_leave);

            if (zzpLeave) {
                this.confirmDialogService
                    .confirm({
                        title: 'ZZP verlof geregistreerd',
                        text: `Op ${formatDate(zzpLeave.begindate, 'EEE d MMM yyyy', 'nl')} is ZZP verlof geregistreerd voor een normale medewerker. Dit is niet toegestaan. Periode verrekenen is niet mogelijk.`,
                        confirm: 'Sluiten',
                        cancel: null,
                    })
                    .then(() => {});
                return;
            }

            const firstOrSecondRow = [
                {
                    realisations: realisationsDay.filter(
                        r => !this.secondLineRealisationHourTypes.includes(r.hourtype)
                    ),
                    settlements: settlementsDay.filter(
                        s => !this.secondLineHourTypeCodes.includes(s.hourtype_code as SettlementHourtype)
                    ),
                },
                {
                    realisations: realisationsDay.filter(r => this.secondLineRealisationHourTypes.includes(r.hourtype)),
                    settlements: settlementsDay.filter(s =>
                        this.secondLineHourTypeCodes.includes(s.hourtype_code as SettlementHourtype)
                    ),
                },
            ];

            firstOrSecondRow.forEach((loop, index) => {
                const realisations = loop.realisations;
                const settlements = loop.settlements;

                let alreadyPaid = (this.user().contract_hours / (this.user().contract_hours / 8)) * 60;

                if (bookdate.getDay() === 6 || bookdate.getDay() === 0) {
                    alreadyPaid = 0;
                }

                let totalMinutes = 0;
                let pause = 0;
                let beginDate = null;
                let endDate = null;

                let dayType = '';

                realisations.forEach(realisation => {
                    const leaveHourTypes = [
                        RealisationHourtype.paid_leave,
                        RealisationHourtype.time_for_time,
                        RealisationHourtype.short_absence,
                        RealisationHourtype.illness,
                        RealisationHourtype.day_off,
                        RealisationHourtype.bank_holiday,
                    ];

                    if (!this.user().overtime || !leaveHourTypes.includes(realisation.hourtype)) {
                        totalMinutes += Utils.minuteDuration(realisation.enddate, realisation.begindate);
                    }

                    pause += realisation.pause;

                    beginDate = beginDate
                        ? new Date(Math.min(Utils.getTimeOrNull(realisation.begindate), Utils.getTimeOrNull(beginDate)))
                        : new Date(realisation.begindate);

                    endDate = new Date(
                        Math.max(Utils.getTimeOrNull(realisation.enddate), Utils.getTimeOrNull(endDate))
                    );

                    if (!endDate || endDate.getTime() < Utils.getTimeOrNull(realisation.enddate)) {
                        endDate = new Date(realisation.enddate);
                    }

                    if (realisation.hourtype === RealisationHourtype.sleep) {
                        dayType = ' ';
                    }
                });

                const hoursToReward = totalMinutes - pause;

                const dayTypeMap = {
                    [SettlementHourtype.workingHours]: 'Werkdag',
                    [SettlementHourtype.moreWorkingHours]: 'Werkdag',
                    ['empty']: 'Weekend',
                    [SettlementHourtype.illness]: 'Ziek',
                    [SettlementHourtype.frost]: 'Spaaruren',
                    [SettlementHourtype.education]: 'Scholing',
                    [SettlementHourtype.paid_leave]: 'Verlof',
                    [SettlementHourtype.short_leave]: 'Onbetaald verlof',
                    [SettlementHourtype.not_paid]: 'Onbetaalde uren',
                    [SettlementHourtype.bank_holiday]: 'Feestdag',
                    [SettlementHourtype.additional_parental]: 'Aanv. geb verl. + ouderschapsv',
                    [SettlementHourtype.special_leave]: 'Bijzonder verlof',
                };

                this.settlementWeekService.OVERTIME_HOURTYPE_CODES.forEach(ht => {
                    dayTypeMap[ht] = 'Werkdag';
                });

                dayTypeMap[SettlementHourtype.sleep] = 'Slaapdag';
                dayTypeMap[SettlementHourtype.sleepWeekend] = 'Slaapdag';

                Object.keys(dayTypeMap).forEach(hourType => {
                    dayType = settlements.find(r => r.hourtype_code === hourType) ? dayTypeMap[hourType] : dayType;

                    if (!this.daytypeCounts.find(dtc => dtc.type === dayTypeMap[hourType])) {
                        this.daytypeCounts.push({ type: dayTypeMap[hourType], count: 0, minutes: 0 });
                    }
                });

                if (
                    dayType === dayTypeMap[SettlementHourtype.workingHours] &&
                    (bookdate.getDay() === 0 || bookdate.getDay() === 6)
                ) {
                    dayType = 'Weekend';
                }

                // Alleen dagen binnen de periode meetellen: de weken lopen erbuiten door, maar de
                // contractdagen-tabel moet de kalendermaand blijven tonen.
                const bookdateInPeriod = this.periodService.isDateInPeriod(bookdate, period);

                if (dayType?.length > 1) {
                    if (bookdateInPeriod) {
                        this.daytypeCounts.find(x => x.type === dayType).count++;

                        Object.keys(dayTypeMap).forEach(hourType => {
                            let minutesOfHourType = 0;

                            settlements
                                .filter(r => r.hourtype_code === hourType)
                                .forEach(settlement => {
                                    minutesOfHourType += settlement.minutes;
                                });

                            let addToType = dayTypeMap[hourType];

                            if (hourType === RealisationHourtype.worktime && dayType === 'Weekend') {
                                addToType = dayType;
                            }

                            this.daytypeCounts.find(x => x.type === addToType).minutes += minutesOfHourType;
                        });
                    }
                } else {
                    if (settlements.find(s => s.hourtype_code === 'F')) {
                        dayType = 'Feestdag';
                    }

                    if (settlements.find(s => ['B2022', 'V', 'KV'].includes(s.hourtype_code))) {
                        dayType = 'Verlof';
                    }

                    if (settlements.find(s => s.hourtype_code === 'Z')) {
                        dayType = 'Ziek';
                    }
                }

                const interruptions = Utils.minuteDuration(endDate, beginDate) - totalMinutes;

                const fg = genFormGroup(
                    dayType,
                    beginDate,
                    endDate,
                    interruptions,
                    totalMinutes,
                    pause,
                    hoursToReward,
                    alreadyPaid
                );

                fg.controls.travelrealisations.setValue(
                    !!realisationsDay.find(r =>
                        [RealisationHourtype.travel_to, RealisationHourtype.travel_back].includes(r.hourtype)
                    )
                );

                fg.disable();

                const fgSecond = genFormGroup(
                    dayType,
                    beginDate,
                    endDate,
                    interruptions,
                    totalMinutes,
                    pause,
                    hoursToReward,
                    alreadyPaid
                );

                fgSecond.disable();

                if (index > 0) {
                    weekDay.fgSecond = fgSecond;
                } else {
                    weekDay.fg = fg;
                }
            });

            weekDay.realisations = realisationsDay;
            weekDay.settlements = [];

            weekDay.date = new Date(bookdate);

            weekDay.inPeriod = this.periodService.isDateInPeriod(weekDay.date, period);

            this.settlementsToForm(settlementsDay, weekDay);

            weekDay.settlements.push(...settlementsDay);
            weekDay.sendToAfas = !!weekDay.settlements.find(s => !!s.sent_to_afas);

            weekDay.week = week;

            weekDay.hasUnapprovedRealisations =
                realisationsDay.filter(
                    r => ((!!r.comment_user_approved && !r.comment_user_approved_handled) || !r.approved) && !r.removed
                )?.length > 0;

            if (weekDay.sendToAfas) {
                week.sendToAfas = true;
            }

            week.weekDays.push(weekDay);

            bookdate.setDate(bookdate.getDate() + 1);

            interval++;

            if (interval > 7) {
                this.weeks.push(week);

                week = new Week();

                interval = 1;
            }
        }

        this.weeks.forEach(week => {
            week.weekDays.forEach(day => {
                this.userSubscriptions.add(
                    day.fg.valueChanges.subscribe(values => {
                        this.calculateTotals(Object.keys(values));
                    })
                );
            });
        });

        this.weeks[0]?.weekDays[0].fg.updateValueAndValidity();

        setTimeout(() => {
            document.body.appendChild(document.createElement('readyforpuppeteer'));
        });
    }

    calculateTotals(formControlKeys: string[]) {
        let totalOvertime = 0;

        // Alleen dagen binnen de periode meetellen. De eerste en laatste week lopen buiten de
        // kalendermaand door - die dagen worden wel verloond, maar niet in de totalen getoond.
        const periodDays = this.weeks.flatMap(w => w.weekDays.filter(weekDay => weekDay.inPeriod));

        for (const key of formControlKeys) {
            const totalFirst = periodDays
                .map(weekDay => weekDay.fgSecond.controls[key].value)
                .reduce((sum, current) => sum + current, 0);

            const totalSecond = periodDays
                .map(weekDay => weekDay.fg.controls[key].value)
                .reduce((sum, current) => sum + current, 0);

            const total = totalFirst + totalSecond;

            this.fcNamePeriodTotalMap.set(key, total);

            if (key.indexOf('overtime') !== -1) {
                totalOvertime += total;
            }
        }

        this.fcNamePeriodTotalMap.set('overtime', totalOvertime);
    }

    ngOnDestroy() {
        this.userSubscriptions?.unsubscribe();
    }
}
